import React from "react";
import { Heading } from "@/components/ui/typography";
import { motion } from "framer-motion";
import { Clock, TrendingUp, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";

const RateExplanation: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const tradingSessions = [
    {
      name: "Morning Session",
      time: "09:00 UTC",
      description: "Asian markets opening, fresh liquidity",
      icon: null,
      active: false
    },
    {
      name: "Day Session",
      time: "15:00 UTC",
      description: "European markets peak, high volume",
      icon: null,
      active: true
    },
    {
      name: "Evening Session",
      time: "21:00 UTC",
      description: "US markets closing, final rates",
      icon: null,
      active: false
    }
  ];

  const stablecoinPairs = [
    { from: "USDC", to: "USD", rate: "1:1", description: "Perfect 1-to-1 conversion" },
    { from: "USDT", to: "USD", rate: "1:1", description: "No conversion fees" },
    { from: "EURC", to: "EUR", rate: "1:1", description: "Direct Euro settlement" }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12">
      <motion.div
        className="text-center space-y-6"
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-display text-display-md sm:text-display-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Smart Rate System
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          We monitor global markets 24/7 and update our rates during the most liquid trading sessions 
          to ensure you always get competitive prices.
        </p>
      </motion.div>

      {/* Trading Sessions */}
      <div className="grid md:grid-cols-3 gap-6">
        {tradingSessions.map((session, index) => (
          <motion.div
            key={session.name}
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <Card
              className={cn(
                "p-6 transition-all duration-240 ease-standard backdrop-blur-sm",
                session.active
                  ? "border-primary/70 bg-primary/10 dark:bg-primary/20 shadow-[0_18px_45px_-24px_rgba(59,130,246,0.35)]"
                  : "border-border/70 hover:border-primary/30 hover:bg-card/80"
              )}
            >
              <CardContent className="p-0 text-center space-y-4">
                <div className="text-4xl">{session.icon}</div>
                <div className="space-y-2">
                  <h3 className="font-display text-heading-md">{session.name}</h3>
                  <div className="flex items-center justify-center gap-2 text-primary font-mono">
                    <Clock className="w-4 h-4" />
                    <span>{session.time}</span>
                  </div>
                  <p className="text-muted-foreground text-sm">{session.description}</p>
                </div>
                {session.active && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Active Now
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Stablecoin Rates */}
      <motion.div
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Card className="p-8 bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-background dark:from-emerald-400/10 dark:via-primary/5 dark:to-slate-950 border-success/25">
          <CardHeader className="p-0 mb-6">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Shield className="w-8 h-8 text-success" />
              Stablecoin Guarantee
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <p className="text-muted-foreground mb-6 text-lg">
              For stablecoins, we offer guaranteed 1:1 conversion rates with no market volatility risk:
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {stablecoinPairs.map((pair, index) => (
                <motion.div
                  key={pair.from}
                  initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={cn(
                    "p-4 rounded-2xl border text-center space-y-2 transition-colors duration-240 ease-standard",
                    "bg-card/90 dark:bg-card/70 backdrop-blur-sm",
                    "border-success/30 dark:border-success/40 shadow-[0_12px_30px_-24px_rgba(15,23,42,0.3)]"
                  )}
                >
                  <div className="font-bold text-lg text-success">
                    {pair.from} → {pair.to}
                  </div>
                  <Heading level={2} className="text-success">{pair.rate}</Heading>
                  <div className="text-sm text-success">{pair.description}</div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Rate Update Process */}
      <motion.div
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="grid md:grid-cols-2 gap-8"
      >
        <Card className="p-8">
          <CardContent className="p-0 space-y-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              <Heading level={3}>Live Market Rates</Heading>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>For volatile cryptocurrencies like Bitcoin and Ethereum:</p>
              <ul className="space-y-2 ml-4">
                <li>• Real-time price feeds from major exchanges</li>
                <li>• Volume-weighted average pricing (VWAP)</li>
                <li>• Deep liquidity from institutional market makers</li>
                <li>• Transparent fee structure (2% developer fee)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="p-8">
          <CardContent className="p-0 space-y-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-primary" />
              <Heading level={3}>Instant Settlement</Heading>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>Once your crypto transaction is confirmed:</p>
              <ul className="space-y-2 ml-4">
                <li>• Automatic conversion at locked-in rates</li>
                <li>• Direct bank transfer initiation</li>
                <li>• Settlement within 1-3 business days</li>
                <li>• Real-time transaction tracking</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export { RateExplanation };